/**
 * Color parsing utilities for handling pasted color values
 */

import { TinyColor } from "@ctrl/tinycolor";

import { isOnlyColorValue } from "./validation";

/**
 * Check if a character is a separator (comma or whitespace).
 */
const isSeparator = (char: string): boolean => {
  return char === "," || char === " " || char === "\t" || char === "\n" || char === "\r";
};

/**
 * Split a list of color values separated by commas, spaces, or newlines,
 * ignoring separators inside parentheses.
 * This allows us to split color values without breaking colors that have
 * commas or spaces inside them (e.g., "rgb(255, 0, 0)").
 *
 * @example
 * splitColorValues("rgb(255, 0, 0), #000")
 * // Returns: ["rgb(255, 0, 0)", "#000"]
 *
 * splitColorValues("#fff #000 #aaa")
 * // Returns: ["#fff", "#000", "#aaa"]
 *
 * splitColorValues("#fff\n#000\n#aaa")
 * // Returns: ["#fff", "#000", "#aaa"]
 */
export const splitColorValues = (str: string): string[] => {
  const result: string[] = [];
  let current = "";
  let depth = 0;

  for (let i = 0; i < str.length; i++) {
    const char = str[i];
    if (char === "(") {
      depth++;
      current += char;
    } else if (char === ")") {
      if (depth > 0) depth--;
      current += char;
    } else if (isSeparator(char) && depth === 0) {
      const trimmed = current.trim();
      if (trimmed) result.push(trimmed);
      current = "";
    } else {
      current += char;
    }
  }

  if (current.trim()) {
    result.push(current.trim());
  }

  return result;
};

/**
 * Convert HSB/HSBA color strings to HSV/HSVA format that TinyColor understands.
 * HSB (Hue, Saturation, Brightness) is the same as HSV (Hue, Saturation, Value),
 * but TinyColor doesn't recognize the "hsb" format string, so we convert it.
 *
 * @example
 * convertHsbToHsv("hsb(180, 50, 75)") // "hsv(180, 50%, 75%)"
 * convertHsbToHsv("hsba(180, 50, 75, 0.5)") // "hsva(180, 50%, 75%, 0.5)"
 * convertHsbToHsv("#FF0000") // "#FF0000" (unchanged)
 */
export const convertHsbToHsv = (str: string): string => {
  // Match hsb/hsba with:
  // - Hue: number with optional decimal and optional angle units (deg|rad|grad|turn)
  // - Saturation/Brightness: number with optional decimal and optional trailing %
  // - Optional alpha: number with optional decimal and optional trailing %
  const hsbRegex =
    /^hsba?\(\s*([+-]?\d*\.?\d+(?:deg|rad|grad|turn)?)\s*,\s*([+-]?\d*\.?\d+%?)\s*,\s*([+-]?\d*\.?\d+%?)\s*(?:,\s*([+-]?\d*\.?\d+%?))?\s*\)$/i;
  const match = str.match(hsbRegex);
  if (match) {
    const hue = match[1].trim();
    const sRaw = match[2].trim();
    const bRaw = match[3].trim();
    const alpha = match[4] !== undefined ? match[4].trim() : undefined;
    const ensurePercent = (value: string): string => {
      return value.endsWith("%") ? value : `${value}%`;
    };
    const sOut = ensurePercent(sRaw);
    const bOut = ensurePercent(bRaw);
    if (alpha !== undefined) {
      return `hsva(${hue}, ${sOut}, ${bOut}, ${alpha})`;
    }
    return `hsv(${hue}, ${sOut}, ${bOut})`;
  }

  return str;
};

/**
 * Validate and extract colors from pasted text.
 * Handles both single colors and comma-separated lists.
 * Returns an array of valid TinyColor instances, or null if no valid colors found.
 *
 * @example
 * validatePastedColors("#FF0000, rgb(0, 255, 0)")
 * // Returns: [TinyColor, TinyColor]
 *
 * validatePastedColors("Just some text")
 * // Returns: null
 */
export const validatePastedColors = (textData: string): TinyColor[] | null => {
  const colorStrings = splitColorValues(textData);
  const convertedColors: TinyColor[] = [];

  for (const colorStr of colorStrings) {
    if (!isOnlyColorValue(colorStr)) {
      return null;
    }
    const converted = convertHsbToHsv(colorStr);
    const color = new TinyColor(converted);
    if (!color.isValid || color.format === "name") {
      return null;
    }
    convertedColors.push(color);
  }

  return convertedColors.length > 0 ? convertedColors : null;
};
